import { useOrganizationList, useUser } from '@clerk/nextjs';
import { Form, Label, ScrollArea, Select, V2 } from '@firmable/common';
import { useTranslation } from '@firmable/i18n';
import { ChevronLeft, Logo } from '@firmable/icons';
import { Routes } from '@web/constants/routes';
import { BillingCountryLabel } from '@web/constants/subscription/billing-country';
import { useSignupConfig } from '@web/query/App/Platform/useSignupConfig';
import { useRouter } from 'next/router';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { OnboardingCardSkeleton } from '../shared';

const supportedCountries = (process.env.NEXT_PUBLIC_SUPPORTED_COUNTRIES?.split(',') ?? []) as AvailableCountries[];

// Accepts "example.com", "www.example.com/path" or a full http(s) URL; a bare domain is the common case.
const WEBSITE_PATTERN = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

const normaliseWebsite = (raw: string) => {
  const trimmed = raw.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isSupportedCountry = (value: string | undefined): value is AvailableCountries =>
  !!value && supportedCountries.includes(value as AvailableCountries);

interface FormValues extends Record<string, unknown> {
  companyName: string;
  website: string;
  preferredBillingCountry: AvailableCountries;
}

export interface CreateCompanyFormProps {
  onBack?: () => void;
}

interface CompanyFormProps extends CreateCompanyFormProps {
  noCcTrialEnabled: boolean;
  defaultCountry: AvailableCountries;
}

const CompanyForm = ({ onBack, noCcTrialEnabled, defaultCountry }: CompanyFormProps) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { user } = useUser();
  const { createOrganization, setActive } = useOrganizationList();

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      companyName: '',
      website: '',
      preferredBillingCountry: defaultCountry,
    },
  });

  const onSubmit = async (data: FormValues) => {
    const trimmedName = data.companyName.trim();

    if (!createOrganization || !setActive || !user) {
      setError('companyName', { message: t('createCompany.errors.unableToCreate') });
      return;
    }

    if (noCcTrialEnabled) {
      // Must be on the user before the organisation exists: OrgCreated reads it off the Clerk
      // webhook and writes billing_country and the website onto the customer row.
      try {
        await user.update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            signUp: {
              ...user.unsafeMetadata.signUp,
              preferredBillingCountry: data.preferredBillingCountry,
              website: normaliseWebsite(data.website),
            },
          },
        });
      } catch {
        setError('companyName', { message: t('createCompany.errors.saveDetailsFailed') });
        return;
      }
    }

    try {
      const org = await createOrganization({ name: trimmedName });
      // Set the new org as active so the session token includes the orgId
      await setActive({ organization: org.id });
      // Toggle on: the trial-or-subscribe step comes first. Off: today's flow, straight to plan selection.
      router.push(noCcTrialEnabled ? Routes.startTrialPath : Routes.planSelectionPath);
    } catch {
      setError('companyName', { message: t('createCompany.errors.createFailed') });
    }
  };

  return (
    <div className="bg-neutral-50 flex flex-col gap-6 items-center w-[400px] sm:w-[440px] px-6 sm:px-10 py-8 rounded-2xl">
      <Logo.Light.FirmableFull height={30} width={142} />

      <div className="flex flex-col gap-2 relative items-center text-center w-full">
        {onBack ? (
          <V2.ButtonIcon
            variant="tertiary"
            size="sm"
            onClick={onBack}
            aria-label={t('actions.back')}
            className="absolute top-[-50px] left-0">
            <ChevronLeft size={16} />
          </V2.ButtonIcon>
        ) : null}
        <h1 className="font-semibold text-[30px] leading-[40px] text-neutral-800">{t('createCompany.title')}</h1>
        <p className="text-sm text-neutral-500">{t('createCompany.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
        <div className="pb-[6px]">
          <Label.Info>{t('createCompany.companyName')}</Label.Info>
          <Form.Input
            aria-label={t('createCompany.companyName')}
            name="companyName"
            register={register}
            options={{ required: t('createCompany.errors.companyNameRequired') }}
            error={errors.companyName}
            placeholder={t('createCompany.companyNamePlaceholder')}
            className="w-full"
            disabled={isSubmitting}
          />
        </div>

        {noCcTrialEnabled ? (
          <>
            <div className="pb-[6px]">
              <Label.Info>{t('createCompany.website')}</Label.Info>
              <Form.Input
                aria-label={t('createCompany.website')}
                name="website"
                register={register}
                options={{
                  required: t('createCompany.errors.websiteRequired'),
                  validate: (value: string) =>
                    WEBSITE_PATTERN.test(value.trim()) || t('createCompany.errors.websiteInvalid'),
                }}
                error={errors.website}
                placeholder={t('createCompany.websitePlaceholder')}
                className="w-full"
                inputMode="url"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex flex-col pb-[6px]">
              <Label.Info>{t('createCompany.billingCountry')}</Label.Info>
              <Controller
                name="preferredBillingCountry"
                control={control}
                rules={{ required: t('createCompany.errors.billingCountryRequired') }}
                render={({ field }) => (
                  <Select.Root name={field.name} value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                    <Select.Trigger
                      aria-label={t('createCompany.billingCountry')}
                      className="w-full h-10 font-normal text-body-large !text-content-dark rounded-radius-md border-stroke-light outline-none">
                      <Select.Value placeholder={t('createCompany.billingCountryPlaceholder')} />
                    </Select.Trigger>
                    <Select.Content className="text-content-dark text-body-large w-[var(--radix-popper-anchor-width)] pl-2 py-2 bg-surface-white">
                      <div className="h-[300px]">
                        <ScrollArea>
                          <div className="pr-3">
                            {supportedCountries.map((country) => (
                              <Select.Item
                                key={country}
                                value={country}
                                isSelected={country === field.value}
                                className="p-2 rounded-lg outline-none min-h-10">
                                {BillingCountryLabel[country]}
                              </Select.Item>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </Select.Content>
                  </Select.Root>
                )}
              />
              {errors.preferredBillingCountry?.message ? (
                <Label.Error>{errors.preferredBillingCountry.message}</Label.Error>
              ) : null}
              <p className="text-body-small text-content-light">{t('createCompany.billingCountryHint')}</p>
            </div>
          </>
        ) : null}

        <V2.Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={isSubmitting}
          className="!w-full !h-11 !bg-brand-1-600 hover:!bg-brand-1-700 !text-white !font-semibold !text-base !rounded-lg shadow-btn-bevel">
          {isSubmitting ? t('createCompany.creating') : t('createCompany.continue')}
        </V2.Button>
      </form>
    </div>
  );
};

export const CreateCompanyForm = ({ onBack }: CreateCompanyFormProps) => {
  const { isLoaded: isOrgListLoaded } = useOrganizationList();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { data: noCcTrialEnabled = false, isLoading: isConfigLoading } = useSignupConfig();

  if (!isOrgListLoaded || !isUserLoaded || !user || isConfigLoading) {
    return <OnboardingCardSkeleton />;
  }

  const ipCountry = user.unsafeMetadata.signUp?.country;

  return (
    <CompanyForm
      onBack={onBack}
      noCcTrialEnabled={noCcTrialEnabled}
      defaultCountry={isSupportedCountry(ipCountry) ? ipCountry : 'AU'}
    />
  );
};
